import { useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import toast from "react-hot-toast"
import axios from "axios"
import moment from "moment"

import { Pagination } from "components"
import Input from "components/forms/input"
import Label from "components/forms/label"
import SelectDescription from "components/forms/select-description"
import OutletFilterSelect from "components/forms/outlet-filter-select"
import { getDefaultOutletFilterValue, resolveOutletFilter } from "helpers/general-helper"

import useBankTransferVerificationRepositories from "repositories/bank-transfer-verification"

const PAYMENT_STATUS_OPTIONS = [
  { id: "ALL", name: "Semua Status Pembayaran" },
  { id: "PENDING_CONFIRMATION", name: "Pending Konfirmasi" },
  { id: "PENDING", name: "Pending" },
  { id: "PAID", name: "Paid (sudah verified)" },
  { id: "REJECTED", name: "Rejected" },
  { id: "VOID", name: "Void" },
]

const PROOF_OPTIONS = [
  { id: "ALL", name: "Semua" },
  { id: "1", name: "Ada Bukti" },
  { id: "0", name: "Belum Upload" },
]

const formatCurrency = (value) =>
  Number(value ?? 0).toLocaleString("id-ID", { minimumFractionDigits: 0, maximumFractionDigits: 2 })

const statusBadgeClass = (status) => {
  const s = String(status || "").toUpperCase()
  if (s === "PAID") return "bg-green-50 text-green-700 border-green-200"
  if (s === "PENDING_CONFIRMATION") return "bg-amber-50 text-amber-700 border-amber-200"
  if (s === "PENDING") return "bg-neutral-50 text-neutral-700 border-neutral-200"
  if (s === "REJECTED") return "bg-rose-50 text-rose-700 border-rose-200"
  if (s === "VOID") return "bg-neutral-100 text-neutral-500 border-neutral-200"
  return "bg-neutral-50 text-neutral-700 border-neutral-200"
}

export default function List() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchText, setSearchText] = useState(searchParams.get("search") || "")
  const [actingKey, setActingKey] = useState(null) // `${order_id}:${bill_number}` saat aksi sedang berjalan
  const [previewProof, setPreviewProof] = useState(null) // URL bukti yang sedang di-preview

  const outletId = searchParams.get("outlet_id") || getDefaultOutletFilterValue()
  // Grup → CSV id (mis. "1,15"); outlet individual → id tunggal.
  const { outlet_id_csv: resolvedOutletCsv } = resolveOutletFilter(outletId)
  const paymentStatus = searchParams.get("payment_status") || "PENDING_CONFIRMATION"
  const hasProof = searchParams.get("has_proof") || "ALL"
  const dateFrom = searchParams.get("date_from") || ""
  const dateTo = searchParams.get("date_to") || ""
  const page = searchParams.get("page") || "1"
  const perPage = searchParams.get("per_page") || "20"

  const { data, isLoading, mutate } = useBankTransferVerificationRepositories({
    search: searchParams.get("search") || undefined,
    outlet_id: resolvedOutletCsv || undefined,
    payment_status: paymentStatus === "ALL" ? undefined : paymentStatus,
    has_proof: hasProof === "ALL" ? undefined : hasProof,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    page,
    per_page: perPage,
  })

  const rows = useMemo(() => {
    if (Array.isArray(data?.data)) return data.data
    return []
  }, [data])

  const paginationLinks = useMemo(() => {
    const links = data?.links ?? []
    return links.map((link) => {
      if (!link?.url) return link
      const nextParams = new URLSearchParams(searchParams)
      try {
        const url = new URL(link.url, window.location.origin)
        const pageNumber = url.searchParams.get("page")
        if (pageNumber) nextParams.set("page", pageNumber)
      } catch (error) {
        const urlParams = new URLSearchParams(String(link.url).split("?")[1] ?? "")
        const pageNumber = urlParams.get("page")
        if (pageNumber) nextParams.set("page", pageNumber)
      }
      return { ...link, url: `?${nextParams.toString()}` }
    })
  }, [data, searchParams])

  const mergeParams = (next) => {
    const current = Object.fromEntries(searchParams.entries())
    const merged = { ...current, ...next }
    Object.keys(merged).forEach((key) => {
      if (merged[key] === null || merged[key] === undefined || merged[key] === "") {
        delete merged[key]
      }
    })
    setSearchParams(merged)
  }

  const submitSearch = () => {
    mergeParams({ search: searchText || null, page: 1 })
  }

  const onApprove = async (row) => {
    const key = `${row.order_id}:${row.bill_number}`
    if (actingKey) return
    if (!window.confirm(`Approve transfer untuk order ${row.order_id}?\nBill: ${row.bill_number}\nNominal: ${formatCurrency(row.amount)}`)) return

    try {
      setActingKey(key)
      const res = await axios.post(
        `${process.env.REACT_APP_INVENTORY_API_URL}/orders/${row.order_id}/approve-payment-row`,
        {
          bill_number: row.bill_number,
          payment_method_type: "bank_transfer",
        }
      )
      toast.success(res.data?.message || "Payment row berhasil di-approve")
      await mutate()
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Gagal approve payment row"
      toast.error(msg)
    } finally {
      setActingKey(null)
    }
  }

  const onReject = async (row) => {
    const key = `${row.order_id}:${row.bill_number}`
    if (actingKey) return
    const reason = window.prompt(
      `Reject order ${row.order_id}?\nBill: ${row.bill_number}\nNominal: ${formatCurrency(row.amount)}\n\nAlasan (wajib):`,
      ""
    )
    if (reason === null) return
    if (!reason.trim()) {
      toast.error("Alasan reject wajib diisi")
      return
    }

    try {
      setActingKey(key)
      const res = await axios.post(
        `${process.env.REACT_APP_INVENTORY_API_URL}/orders/${row.order_id}/reject`,
        { reason: reason.trim() }
      )
      toast.success(res.data?.message || "Order di-set REJECTED")
      await mutate()
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Gagal reject order"
      toast.error(msg)
    } finally {
      setActingKey(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="p-5 bg-white border rounded-2xl border-neutral-200">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">Verifikasi Transfer Bank</h2>
            <p className="mt-1 text-sm text-neutral-500">
              Daftar pembayaran via transfer bank. HO bisa cek bukti dan approve / reject untuk memastikan transfer-nya legit.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 mt-5 md:grid-cols-6">
          <div className="md:col-span-2">
            <Label value="Cari" />
            <Input
              type="text"
              value={searchText}
              placeholder="Order ID / nama customer / no. HP..."
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitSearch() } }}
            />
          </div>
          <div>
            <Label value="Outlet" />
            <OutletFilterSelect
              value={outletId}
              onChange={(v) => mergeParams({ outlet_id: v, page: 1 })}
              className="w-full mt-1 p-2 text-sm transition border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-50 disabled:opacity-75"
            />
          </div>
          <div>
            <Label value="Status Pembayaran" />
            <SelectDescription
              selection={PAYMENT_STATUS_OPTIONS}
              value={paymentStatus}
              onChange={(value) => mergeParams({ payment_status: value || "ALL", page: 1 })}
              keyValue={(v) => v.id}
              title={(v) => v.name}
              description={() => ""}
              placeholder="Status..."
            />
          </div>
          <div>
            <Label value="Bukti" />
            <SelectDescription
              selection={PROOF_OPTIONS}
              value={hasProof}
              onChange={(value) => mergeParams({ has_proof: value || "ALL", page: 1 })}
              keyValue={(v) => v.id}
              title={(v) => v.name}
              description={() => ""}
              placeholder="Bukti..."
            />
          </div>
          <div>
            <Label value="Per Halaman" />
            <select
              value={perPage}
              onChange={(event) => mergeParams({ per_page: event.target.value, page: 1 })}
              className="w-full px-3 py-2 mt-1 text-sm border rounded-lg border-neutral-300 focus:outline-none focus:ring focus:ring-neutral-200"
            >
              {[10, 20, 50, 100].map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <Label value="Tanggal Dari" />
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => mergeParams({ date_from: e.target.value, page: 1 })}
            />
          </div>
          <div>
            <Label value="Tanggal Sampai" />
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => mergeParams({ date_to: e.target.value, page: 1 })}
            />
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-2xl border-neutral-200">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-4 py-3 font-medium text-left text-neutral-500 whitespace-nowrap text-xs uppercase">Tgl Bayar</th>
                <th className="px-4 py-3 font-medium text-left text-neutral-500 whitespace-nowrap text-xs uppercase">Order</th>
                <th className="px-4 py-3 font-medium text-left text-neutral-500 whitespace-nowrap text-xs uppercase">Customer</th>
                <th className="px-4 py-3 font-medium text-left text-neutral-500 whitespace-nowrap text-xs uppercase">Bill</th>
                <th className="px-4 py-3 font-medium text-left text-neutral-500 whitespace-nowrap text-xs uppercase">Bank / Rekening Tujuan</th>
                <th className="px-4 py-3 font-medium text-right text-neutral-500 whitespace-nowrap text-xs uppercase">Nominal</th>
                <th className="px-4 py-3 font-medium text-left text-neutral-500 whitespace-nowrap text-xs uppercase">Bukti</th>
                <th className="px-4 py-3 font-medium text-left text-neutral-500 whitespace-nowrap text-xs uppercase">Status</th>
                <th className="px-4 py-3 font-medium text-right text-neutral-500 whitespace-nowrap text-xs uppercase">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-neutral-500">Memuat data...</td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-neutral-500">
                    Tidak ada payment transfer untuk filter yang dipilih.
                  </td>
                </tr>
              )}
              {!isLoading && rows.map((row) => {
                const order = row.order || {}
                const proofUrl = row.payment_proof_url
                const accountNumber = row.account_number || row.payment_method?.account_number || "-"
                const bankLabel = row.payment_channel || row.payment_type || row.payment_method?.name || "-"
                const status = String(row.payment_status || "").toUpperCase()
                const key = `${row.order_id}:${row.bill_number}`
                const isActing = actingKey === key
                const canApprove = status !== "PAID" && status !== "REJECTED" && status !== "VOID"
                const canReject = status !== "PAID" && status !== "REJECTED" && status !== "VOID"

                return (
                  <tr key={`${row.id}-${row.bill_number}`} className="border-t border-neutral-200 hover:bg-brand-50 transition-colors">
                    <td className="px-4 py-3 text-neutral-800 whitespace-nowrap">
                      {row.paid_date ? moment(row.paid_date).format("DD/MM/YYYY HH:mm") : "-"}
                      <div className="text-[11px] text-neutral-500">
                        Upload: {row.payment_proof_uploaded_at ? moment(row.payment_proof_uploaded_at).format("DD/MM/YYYY HH:mm") : "-"}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-neutral-900 whitespace-nowrap">
                      <div className="break-all">{row.order_id}</div>
                      <div className="text-[11px] text-neutral-500">
                        {order.outlet_id ? `Outlet ${order.outlet_id}` : ""}
                        {order.order_date ? ` · ${moment(order.order_date).format("DD/MM/YYYY")}` : ""}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-neutral-800">
                      <div>{order.customer || "-"}</div>
                      <div className="text-[11px] text-neutral-500">{order.customer_phone || ""}</div>
                    </td>
                    <td className="px-4 py-3 text-neutral-800 whitespace-nowrap">
                      <div className="break-all text-[11px]">{row.bill_number || "-"}</div>
                      {row.installment_count > 1 && (
                        <div className="text-[11px] text-neutral-500">
                          Cicilan {row.installment_number}/{row.installment_count}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-neutral-800">
                      <div>{bankLabel}</div>
                      <div className="text-[11px] text-neutral-500">Rek: {accountNumber}</div>
                    </td>
                    <td className="px-4 py-3 font-semibold text-right text-neutral-900 whitespace-nowrap">
                      {formatCurrency(row.amount)}
                    </td>
                    <td className="px-4 py-3 text-neutral-800">
                      {proofUrl ? (
                        <button
                          type="button"
                          onClick={() => setPreviewProof(proofUrl)}
                          className="px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
                        >
                          Lihat Bukti
                        </button>
                      ) : (
                        <span className="text-xs text-neutral-400 italic">Belum upload</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${statusBadgeClass(status)}`}>
                        {status || "-"}
                      </span>
                      <div className="text-[11px] text-neutral-500 mt-1">
                        Order: {order.payment_status || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          disabled={!canApprove || isActing}
                          onClick={() => onApprove(row)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${
                            !canApprove || isActing
                              ? "bg-neutral-100 text-neutral-400 border-neutral-200 cursor-not-allowed"
                              : "bg-green-600 text-white border-green-600 hover:bg-green-700"
                          }`}
                        >
                          {isActing ? "..." : "Approve"}
                        </button>
                        <button
                          type="button"
                          disabled={!canReject || isActing}
                          onClick={() => onReject(row)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${
                            !canReject || isActing
                              ? "bg-neutral-100 text-neutral-400 border-neutral-200 cursor-not-allowed"
                              : "bg-rose-600 text-white border-rose-600 hover:bg-rose-700"
                          }`}
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-4 border-t border-neutral-200">
          <Pagination
            links={paginationLinks}
            from={data?.from}
            to={data?.to}
            total={data?.total}
          />
        </div>
      </div>

      {previewProof && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          onClick={() => setPreviewProof(null)}
        >
          <div
            className="relative max-w-3xl max-h-[90vh] bg-white rounded-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
              <div className="text-sm font-semibold text-neutral-900">Bukti Transfer</div>
              <div className="flex items-center gap-2">
                <a
                  href={previewProof}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 text-xs font-medium text-neutral-700 border border-neutral-200 rounded-lg hover:bg-neutral-50"
                >
                  Buka di Tab Baru
                </a>
                <button
                  type="button"
                  onClick={() => setPreviewProof(null)}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-brand-900 rounded-lg hover:bg-brand-800"
                >
                  Tutup
                </button>
              </div>
            </div>
            <div className="p-4 overflow-auto max-h-[80vh]">
              <img
                src={previewProof}
                alt="Bukti Transfer"
                className="max-w-full max-h-[75vh] object-contain mx-auto"
                onError={(e) => {
                  e.currentTarget.style.display = "none"
                  e.currentTarget.parentElement.innerHTML = `
                    <div class="text-sm text-neutral-500 text-center py-8">
                      Bukti tidak dapat ditampilkan sebagai gambar.<br/>
                      <a href="${previewProof}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">Buka file</a>
                    </div>`
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
